/**
 * Copyright (c) 2019-2021 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { Loci } from '../mol-model/loci';
import { StructureElement } from '../mol-model/structure';
import { Script } from '../mol-script/script';
export { Transparency };

function Transparency(kind, layers) {
  return {
    kind: kind,
    layers: layers
  };
}

(function (Transparency) {
  Transparency.Empty = {
    kind: 'empty-loci',
    layers: []
  };

  function areEqual(tA, tB) {
    if (tA.layers.length === 0 && tB.layers.length === 0) return true;
    if (tA.layers.length !== tB.layers.length) return false;

    for (var i = 0, il = tA.layers.length; i < il; ++i) {
      if (tA.layers[i].value !== tB.layers[i].value) return false;
      if (!Loci.areEqual(tA.layers[i].loci, tB.layers[i].loci)) return false;
    }

    return true;
  }

  Transparency.areEqual = areEqual;

  function isEmpty(transparency) {
    return transparency.layers.length === 0;
  }

  Transparency.isEmpty = isEmpty;

  function remap(transparency, structure) {
    if (transparency.kind === 'element-loci') {
      var layers = [];

      for (var _i = 0, _a = transparency.layers; _i < _a.length; _i++) {
        var layer = _a[_i];
        var loci = StructureElement.Loci.remap(layer.loci, structure);

        if (!StructureElement.Loci.isEmpty(loci)) {
          layers.push({
            loci: loci,
            value: layer.value
          });
        }
      }

      return {
        kind: 'element-loci',
        layers: layers
      };
    } else {
      return transparency;
    }
  }

  Transparency.remap = remap;

  function merge(transparency) {
    if (isEmpty(transparency)) return transparency;

    if (transparency.kind === 'element-loci') {
      var structure = transparency.layers[0].loci.structure;
      var map = new Map();
      var shadowed = StructureElement.Loci.none(structure);

      for (var i = 0, il = transparency.layers.length; i < il; ++i) {
        var _a = transparency.layers[il - i - 1],
            loci = _a.loci,
            value = _a.value; // process from end

        loci = StructureElement.Loci.subtract(loci, shadowed);
        shadowed = StructureElement.Loci.union(loci, shadowed);

        if (!StructureElement.Loci.isEmpty(loci)) {
          if (map.has(value)) {
            loci = StructureElement.Loci.union(loci, map.get(value));
          }

          map.set(value, loci);
        }
      }

      var layers_1 = [];
      map.forEach(function (loci, value) {
        layers_1.push({
          loci: loci,
          value: value
        });
      });
      return {
        kind: 'element-loci',
        layers: layers_1
      };
    } else {
      return transparency;
    }
  }

  Transparency.merge = merge;

  function filter(transparency, filter) {
    if (isEmpty(transparency)) return transparency;

    if (transparency.kind === 'element-loci') {
      var structure = transparency.layers[0].loci.structure;
      var layers = [];

      for (var _i = 0, _a = transparency.layers; _i < _a.length; _i++) {
        var layer = _a[_i];
        var loci = layer.loci,
            value = layer.value; // filter by first map to the `filter` structure and
        // then map back to the original structure of the transparency loci

        var filtered = StructureElement.Loci.remap(loci, filter);
        loci = StructureElement.Loci.remap(filtered, structure);

        if (!StructureElement.Loci.isEmpty(loci)) {
          layers.push({
            loci: loci,
            value: value
          });
        }
      }

      return {
        kind: 'element-loci',
        layers: layers
      };
    } else {
      return transparency;
    }
  }

  Transparency.filter = filter;

  function ofScript(scriptLayers, structure) {
    var layers = [];

    for (var i = 0, il = scriptLayers.length; i < il; ++i) {
      var _a = scriptLayers[i],
          script = _a.script,
          value = _a.value;
      var loci = Script.toLoci(script, structure);

      if (!StructureElement.Loci.isEmpty(loci)) {
        layers.push({
          loci: loci,
          value: value
        });
      }
    }

    return {
      kind: 'element-loci',
      layers: layers
    };
  }

  Transparency.ofScript = ofScript;

  function ofBundle(bundleLayers, structure) {
    var layers = [];

    for (var i = 0, il = bundleLayers.length; i < il; ++i) {
      var _a = bundleLayers[i],
          bundle = _a.bundle,
          value = _a.value;
      var loci = StructureElement.Bundle.toLoci(bundle, structure.root);
      layers.push({
        loci: loci,
        value: value
      });
    }

    return {
      kind: 'element-loci',
      layers: layers
    };
  }

  Transparency.ofBundle = ofBundle;

  function toBundle(transparency) {
    var layers = [];

    for (var i = 0, il = transparency.layers.length; i < il; ++i) {
      var _a = transparency.layers[i],
          loci = _a.loci,
          value = _a.value;
      var bundle = StructureElement.Bundle.fromLoci(loci);
      layers.push({
        bundle: bundle,
        value: value
      });
    }

    return {
      kind: 'element-loci',
      layers: layers
    };
  }

  Transparency.toBundle = toBundle;
})(Transparency || (Transparency = {}));